import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useCreateNegotiation,
  type CreateNegotiationInput,
  type CustomerJourney,
} from '@/api/customer-journey';
import type { AttachmentPayload } from '@/api/uploads';
import { env } from '@/config/env';
import { errorMessage } from './sale-process-error';
import { NEGOTIATION_TYPE_OPTIONS, NEGOTIATION_PRIORITY_OPTIONS, NEGOTIATION_STAGE_OPTIONS } from './negotiation-options';
import { AssigneesField, AttachmentsField } from './negotiation-form-fields';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

/** Local `YYYY-MM-DD` for a date, no UTC shift (matches the date input value). */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Create-negotiation dialog. Collects the fields the legacy CreateNegotiation
 * form requires and posts the full `contract_negotiation_*` body so the request
 * matches the v4 backend exactly. The backend REQUIRES a non-empty assignee list
 * and a deadline; assignees default to `env.processAssigneeIds` (mirrors the
 * legacy `APP_PROCESS_ASSIGNEE_IDS` seed) and the deadline defaults to today.
 */
export function CreateNegotiationDialog({
  customerId,
  journey,
  open,
  onOpenChange,
  onCreated,
  step,
}: {
  customerId: number;
  journey: CustomerJourney;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (ok: boolean, detail?: string) => void;
  /** Journey step to attach the negotiation to (a board column pins its step). */
  step?: string;
}) {
  const mutation = useCreateNegotiation();
  const [title, setTitle] = useState('');
  const [type, setType] = useState('quote_request');
  const [priority, setPriority] = useState('medium');
  const [stage, setStage] = useState('contract_preparation');
  const [value, setValue] = useState('');
  const [deadline, setDeadline] = useState(todayIso());
  const [content, setContent] = useState('');
  const [notes, setNotes] = useState('');
  const [assignees, setAssignees] = useState<number[]>(env.processAssigneeIds);
  const [attachments, setAttachments] = useState<AttachmentPayload[]>([]);

  useEffect(() => {
    if (open) {
      setTitle('');
      setType('quote_request');
      setPriority('medium');
      setStage('contract_preparation');
      setValue('');
      setDeadline(todayIso());
      setContent('');
      setNotes('');
      setAssignees(env.processAssigneeIds);
      setAttachments([]);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = (next: boolean) => {
    if (mutation.isPending) return;
    onOpenChange(next);
  };

  const submit = () => {
    if (!title.trim()) return;
    const parsedValue = value.trim() ? Number(value) : 0;
    // Backend wants a full RFC3339 timestamp for the deadline, not a bare date.
    const deadlineIso = new Date(`${deadline || todayIso()}T00:00:00`).toISOString();
    const payload: CreateNegotiationInput = {
      contract_negotiation_title: title.trim(),
      contract_negotiation_type: type,
      contract_negotiation_priority: priority,
      contract_negotiation_stage: stage,
      contract_negotiation_status: 'draft',
      contract_negotiation_value: Number.isFinite(parsedValue) ? parsedValue : 0,
      contract_negotiation_progress: 0,
      contract_negotiation_content: content.trim(),
      contract_negotiation_notes: notes.trim(),
      contract_negotiation_deadline: deadlineIso,
      // Seeded from env (legacy APP_PROCESS_ASSIGNEE_IDS), user-editable below.
      contract_negotiation_assignees: assignees,
      contract_negotiation_customer_id: customerId,
      contract_negotiation_customer_journey_id: journey.customer_journey_id,
      contract_negotiation_customer_journey_step: step || journey.customer_journey_step || 'contract',
      attachments,
    };
    mutation.mutate(payload, {
      onSuccess: () => {
        onCreated(true);
        onOpenChange(false);
      },
      onError: (err) => onCreated(false, errorMessage(err)),
    });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Create negotiation</DialogTitle>
          <DialogDescription>Start a price negotiation for this journey.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? (
            <Alert tone="danger" title="Could not create negotiation">{errorMessage(mutation.error)}</Alert>
          ) : null}
          <Field label="Title" required>
            {(p) => (
              <Input
                {...p}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter negotiation title"
              />
            )}
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label="Type">
              {(p) => (
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NEGOTIATION_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Priority">
              {(p) => (
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NEGOTIATION_PRIORITY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
            <Field label="Stage">
              {(p) => (
                <Select value={stage} onValueChange={setStage}>
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NEGOTIATION_STAGE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Value (₫)">
              {(p) => (
                <NumericInput {...p} value={value} onChange={(e) => setValue(e.target.value)} placeholder="0" />
              )}
            </Field>
          </div>
          <Field label="Deadline">
            {(p) => <DateInput {...p} value={deadline} onChange={(e) => setDeadline(e.target.value)} />}
          </Field>
          <AssigneesField value={assignees} onChange={setAssignees} />
          <Field label="Content">
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter negotiation content"
              />
            )}
          </Field>
          <Field label="Notes">
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Enter notes"
              />
            )}
          </Field>
          <AttachmentsField value={attachments} onChange={setAttachments} />
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={() => close(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!title.trim()} onClick={submit}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateNegotiationDialog;
